import { useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import Navigation from '../Navbar/Navbar';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { Container, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import './AddEquipment.css';


const apiUrl = "/api";


function AddEquipment() {

  const [equipmentDetails, setEquipmentDetails] = useState({
    id: '',
    qrid: '',
    Type: '',
    Make: '',
    Model: '',
    Serial: '',
    coa: '',
    os: '',
    Location: '',
    custodian: '',
    purchase_date: '',
    cost_value: '',
    warranty_expiry: '',
    status: 'Received',
    details: '',
  });

  const [showAlert, setShowAlert] = useState(false);
  const location = useLocation();

  // Robust parameter extraction
  let searchParams = new URLSearchParams(location.search);

  // Fallback: sometimes HashRouter puts params in hash but useLocation().search is empty
  // e.g. /#/inv?dbid=1 might result in location.pathname="/inv" and location.search="" depending on version
  if (!location.search && location.hash.includes('?')) {
    const hashParts = location.hash.split('?');
    if (hashParts.length > 1) {
      searchParams = new URLSearchParams('?' + hashParts[1]);
    }
  } else if (!location.search && window.location.hash.includes('?')) {
    // Last resort check window.location
    const hashParts = window.location.hash.split('?');
    if (hashParts.length > 1) {
      searchParams = new URLSearchParams('?' + hashParts[1]);
    }
  }

  const id = searchParams.get('id');
  const dbid = searchParams.get('dbid');

  console.log('AddEquipment params:', { id, dbid, search: location.search, hash: location.hash });

  const [detailsLoaded, setDetailsLoaded] = useState(false);


  useEffect(() => {
    if (!localStorage.getItem('token')) {
      // Redirect to login page if not logged in 
      window.location.href = '#/login';
      return null;
    }

    // Fetch equipment details if we have an ID (edit mode)
    if (id || dbid) {
      const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
      const token = localStorage.getItem('token');

      // Determine fetch URL based on available ID
      const fetchUrl = dbid
        ? `${apiUrl}/item/${dbid}?tenant_id=${tenantId}`
        : `${apiUrl}/inv/${id}?tenant_id=${tenantId}`; // Note: legacy endpoint /inv/:id needs update to support tenant_id param if not already supported
      console.log('Fetching equipment from:', fetchUrl);

      axios.get(fetchUrl, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((response) => {
          console.log('Equipment fetch successful, payload:', response.data);
          // Handle both array (common) and single object (fallback) responses
          const equipmentData = Array.isArray(response.data) ? response.data[0] : response.data;

          if (equipmentData) {
            console.log('Setting equipment details for ID:', equipmentData.id);
            // Check if this is a computer/laptop to show extra fields
            const isComputerOrLaptop = ['Computer', 'Laptop', 'COMPUTER', 'LAPTOP'].includes(equipmentData.Type);
            if (isComputerOrLaptop) {
              setDetailsLoaded(true);
            }

            // Set initial equipment details from main items table
            setEquipmentDetails({
              id: equipmentData.id,
              qrid: equipmentData.qrid || '',
              Type: equipmentData.Type || '',
              Make: equipmentData.Make || '',
              Model: equipmentData.Model || '',
              Serial: equipmentData.Serial || '',
              coa: '',
              os: '',
              Location: equipmentData.Location || '',
              custodian: equipmentData.custodian || '',
              purchase_date: equipmentData.purchase_date ? equipmentData.purchase_date.split('T')[0] : '',
              cost_value: equipmentData.cost_value || '',
              warranty_expiry: equipmentData.warranty_expiry ? equipmentData.warranty_expiry.split('T')[0] : '',
              status: equipmentData.status || 'Received',
              details: '',
            });

            // Fetch extra details for computers/laptops using the QRID from the fetched data
            if (isComputerOrLaptop && equipmentData.qrid) {
              axios.get(apiUrl + `/extra-details/${equipmentData.qrid}`, {
                headers: { Authorization: `Bearer ${token}` }
              }).then((extraResponse) => {
                const extraDetails = Array.isArray(extraResponse.data) ? extraResponse.data[0] : extraResponse.data;
                if (extraDetails) {
                  setEquipmentDetails((prevDetails) => ({
                    ...prevDetails,
                    coa: extraDetails.COA || '',
                    os: extraDetails.OS || '',
                    details: extraDetails.DETAILS || '',
                  }));
                }
              }).catch((error) => {
                console.error('Error fetching extra details:', error);
              });
            }
          } else {
            console.error('No equipment data found in response');
            setShowAlert(true);
          }
        })
        .catch((error) => {
          console.error('Error fetching equipment:', error);
        });
    }
  }, [id, dbid]);

  useEffect(() => {
    // Set defaults if creating new or if fields are empty
    if (!id && !dbid) {
      setEquipmentDetails(prev => ({
        ...prev,
        purchase_date: prev.purchase_date || new Date().toISOString().split('T')[0],
        custodian: prev.custodian || 'IT Store',
        cost_value: prev.cost_value || '300'
      }));
    }
  }, [id, dbid]);

  // Empty dependency array ensures that this effect runs only once on component mount
  //console.log(equipmentDetails);  

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEquipmentDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleAddOrUpdateEquipment = () => {
    const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;

    let equipmentData;
    if (equipmentDetails.id) {
      // For updates, send all fields
      equipmentData = { ...equipmentDetails, tenant_id: tenantId };
    } else {
      // For creating new items, only send the fields expected by the API
      equipmentData = {
        Type: equipmentDetails.Type,
        Make: equipmentDetails.Make,
        Model: equipmentDetails.Model,
        Serial: equipmentDetails.Serial,
        Location: equipmentDetails.Location,
        qrid: equipmentDetails.qrid,
        custodian: equipmentDetails.custodian,
        purchase_date: equipmentDetails.purchase_date,
        cost_value: equipmentDetails.cost_value,
        warranty_expiry: equipmentDetails.warranty_expiry,
        status: equipmentDetails.status,
        tenant_id: tenantId
      };
    }

    const apiEndpoint = equipmentDetails.id ? 'update-item' : 'create-new-item';
    const token = localStorage.getItem('token');

    // Use PUT for updates, POST for creates
    const axiosMethod = equipmentDetails.id ? axios.put : axios.post;

    axiosMethod(apiUrl + `/${apiEndpoint}`, equipmentData, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => {
        setShowAlert(true);
        if (equipmentDetails.Type === "Computer" || equipmentDetails.Type === "Laptop"
          || equipmentDetails.Type === "COMPUTER" || equipmentDetails.Type === "LAPTOP"
        ) {
          if (equipmentDetails.coa) {
            console.log("Updating after loaded Detail:" + equipmentDetails.Type);
            const token = localStorage.getItem('token');
            axios.post(apiUrl + '/update-item-extra-details', equipmentData, {
              headers: { Authorization: `Bearer ${token}` }
            }).then
              ((response) => {
                console.log("Extra Details updated: " + response);
                window.location.reload();
              })


          }
          else {
            const token = localStorage.getItem('token');
            axios.post(apiUrl + '/inv-extradetails', equipmentData, {
              headers: { Authorization: `Bearer ${token}` }
            }).then
              ((response) => {
                console.log("Extra Details Added: " + response);
                window.location.reload();
              })
          }
          //window.location.reload();
        }
        window.location.reload();
      })
      .catch((err) => {
        console.error(err);
        if (err.response && err.response.data && err.response.data.error) {
          alert(err.response.data.error); // Show validation error from backend
        }
        setShowAlert(false);
      });

  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleAddOrUpdateEquipment();
  };
  console.log(equipmentDetails);
  return (
    <div>
      <Navigation />
      <Container fluid className="mt-4 px-4">
        <Row className="justify-content-center">
          <Col md={equipmentDetails.id ? 8 : 10}>
            <div className="add-equipment-container p-0" style={{ minHeight: 'auto', background: 'transparent' }}>
              <div className="add-equipment-box w-100">
                <h1 className="add-equipment-heading">
                  {equipmentDetails.id ? 'Update Equipment' : 'Add Equipment'}
                </h1>
                <Alert variant="success" show={showAlert} onClose={() => setShowAlert(false)} dismissible>
                  Equipment {equipmentDetails.id ? 'updated' : 'created!'}
                </Alert>
                <Form onSubmit={handleSubmit} >
                  <Form.Group controlId="equipmentStatus" className="mb-3">
                    <Form.Label>Asset Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={equipmentDetails.status}
                      onChange={handleInputChange}
                    >
                      <option value="Received">Received (Initial Intake)</option>
                      <option value="InPrep">In Preparation (Tagging/Imaging)</option>
                      <option
                        value="ReadyForDispatch"
                        disabled={
                          !equipmentDetails.qrid ||
                          !equipmentDetails.Type ||
                          !equipmentDetails.Make ||
                          !equipmentDetails.Model ||
                          !equipmentDetails.Serial ||
                          !equipmentDetails.Location ||
                          !equipmentDetails.purchase_date ||
                          !equipmentDetails.cost_value ||
                          !equipmentDetails.warranty_expiry ||
                          ((['Computer', 'Laptop', 'COMPUTER', 'LAPTOP'].includes(equipmentDetails.Type)) && (!equipmentDetails.coa || !equipmentDetails.os))
                        }
                      >
                        Ready for Dispatch
                      </option>
                      <option value="Deployed">Deployed</option>
                      <option value="Retired">Retired</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {(!equipmentDetails.qrid ||
                        !equipmentDetails.Type ||
                        !equipmentDetails.Make ||
                        !equipmentDetails.Model ||
                        !equipmentDetails.Serial ||
                        !equipmentDetails.Location ||
                        !equipmentDetails.purchase_date ||
                        !equipmentDetails.cost_value ||
                        !equipmentDetails.warranty_expiry ||
                        ((['Computer', 'Laptop', 'COMPUTER', 'LAPTOP'].includes(equipmentDetails.Type)) && (!equipmentDetails.coa || !equipmentDetails.os)))
                        ? "* 'Ready for Dispatch' requires all asset details to be filled (QRID, Make, Model, Serial, Location, Dates, Cost, and Tech Specs for Computers)."
                        : ""}
                    </Form.Text>
                  </Form.Group>

                  <h5>Identification</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group controlId="equipmentId">
                        <Form.Label>Equipment QR-ID</Form.Label>
                        <Form.Control
                          type="text"
                          name="qrid"
                          value={equipmentDetails.qrid}
                          onChange={handleInputChange}
                          placeholder="Enter Equipment ID"
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="equipmentSerial">
                        <Form.Label>Serial</Form.Label>
                        <Form.Control
                          type="text"
                          name="Serial"
                          // required  // Relax client-side required to allow initial intake flow
                          value={equipmentDetails.Serial}
                          onChange={handleInputChange}
                          placeholder="Enter Serial"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <h5 className="mt-3">Specs & Location</h5>
                  <div className="row">
                    <div className="col-md-4">
                      <Form.Group controlId="equipmentType">
                        <Form.Label>Type</Form.Label>
                        <Form.Select
                          required
                          name="Type"
                          value={equipmentDetails.Type || ' '}
                          onChange={handleInputChange}
                        >
                          <option >{equipmentDetails.Type} </option>
                          <option value="Computer">Computer</option>
                          <option value="Laptop">Laptop</option>
                          <option value="Monitor">Monitor</option>
                          <option value="Router">Router/Managed Switch</option>
                          <option value="Mouse">Mouse</option>
                          <option value="Keyboard">Keyboard</option>
                          <option value="Ethernet Cable">Ethernet Cable</option>
                          <option value="USB A-B Cable">USB A-B Cable</option>
                          <option value="USB Headset">USB Headset</option>
                          <option value="VGA Cable / Display Cable">VGA Cable / Display Cable</option>
                          <option value="DVI Cable / Display Cable">DVI Cable / Display Cable</option>
                          <option value="Power Cable">Power Cable</option>
                          <option value="DisplayPort Cable">DisplayPort Cable</option>
                          <option value="PC's Power Cable">PC's Power Cable</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group controlId="equipmentMake">
                        <Form.Label>Make</Form.Label>
                        <Form.Control
                          type="text"
                          name="Make"
                          value={equipmentDetails.Make}
                          onChange={handleInputChange}
                          placeholder="Enter Make"
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-4">
                      <Form.Group controlId="equipmentModel">
                        <Form.Label>Model</Form.Label>
                        <Form.Control
                          type="text"
                          name="Model"
                          value={equipmentDetails.Model}
                          onChange={handleInputChange}
                          placeholder="Enter Model"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group controlId="equipmentFLocation" className="mt-2">
                    <Form.Label>Functional Location</Form.Label>
                    <Form.Control
                      type="text"
                      name="Location"
                      value={equipmentDetails.Location}
                      onChange={handleInputChange}
                      placeholder="Enter Functional Location"
                    />
                  </Form.Group>

                  <h5 className="mt-3">Financial & Warranty</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group controlId="purchaseDate">
                        <Form.Label>Purchase Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="purchase_date"
                          value={equipmentDetails.purchase_date}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="costValue">
                        <Form.Label>Cost / Value</Form.Label>
                        <Form.Control
                          type="number"
                          name="cost_value"
                          value={equipmentDetails.cost_value}
                          onChange={handleInputChange}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group controlId="warrantyExpiry">
                        <Form.Label>Warranty Expiry</Form.Label>
                        <Form.Control
                          type="date"
                          name="warranty_expiry"
                          value={equipmentDetails.warranty_expiry}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="custodian">
                        <Form.Label>Primary Custodian (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="custodian"
                          value={equipmentDetails.custodian}
                          onChange={handleInputChange}
                          placeholder="e.g. IT Store, or Staff Name"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  {(equipmentDetails.Type === "Computer" || equipmentDetails.Type === "Laptop"
                    || equipmentDetails.Type === "COMPUTER" || equipmentDetails.Type === "LAPTOP" || detailsLoaded) && (
                      <div className="mt-3">
                        <h5>Technical Specs</h5>
                        <Form.Group controlId="equipmentCOA">
                          <Form.Label>COA</Form.Label>
                          <Form.Control
                            type="text"
                            name="coa"
                            value={equipmentDetails.coa}
                            onChange={handleInputChange}
                            placeholder="Enter COA"
                          />
                        </Form.Group>
                        <Form.Group controlId="equipmentOS">
                          <Form.Label>OS</Form.Label>
                          <Form.Control
                            type="text"
                            name="os"
                            value={equipmentDetails.os}
                            onChange={handleInputChange}
                            placeholder="Enter OS"
                          />
                        </Form.Group>

                        <Form.Group controlId="equipmentDetails">
                          <Form.Label>Details</Form.Label>
                          <Form.Control
                            as="textarea" // Set the input type to textarea
                            name="details"
                            value={equipmentDetails.details}
                            onChange={handleInputChange}
                            placeholder="Enter Details"
                            style={{ height: '100px' }} // Set the textarea height to 100px
                          />
                        </Form.Group>
                      </div>
                    )}

                  <Button variant="primary" className="custom-button mt-4" type="submit">
                    {equipmentDetails.id ? 'Update Equipment' : 'Add Equipment'}
                  </Button>
                </Form>
              </div>
            </div>
          </Col>

          {/* Asset History Sidebar - Only visible in Edit Mode */}
          {equipmentDetails.id && (
            <Col md={4}>
              <div className="add-equipment-container p-0" style={{ minHeight: 'auto', background: 'transparent' }}>
                <div className="add-equipment-box w-100 h-100">
                  <h4 className="mb-3">Asset History</h4>
                  <AssetHistory assetId={equipmentDetails.id} />
                </div>
              </div>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );

}

function AssetHistory({ assetId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${apiUrl}/item/${assetId}/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(res.data);
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setLoading(false);
      }
    };

    if (assetId) fetchHistory();
  }, [assetId]);

  if (loading) return <div className="text-muted">Loading history...</div>;
  if (history.length === 0) return <div className="text-muted">No history audits found for this asset.</div>;

  return (
    <div className="table-responsive">
      <table className="table table-sm table-striped">
        <thead>
          <tr>
            <th>Date</th>
            <th>Action</th>
            <th>Details</th>
            <th>User</th>
          </tr>
        </thead>
        <tbody>
          {history.map((record) => (
            <tr key={record.id}>
              <td>{new Date(record.created_at).toLocaleString()}</td>
              <td>
                <span className={`badge ${record.action === 'create' ? 'bg-success' : 'bg-primary'}`}>
                  {record.action.toUpperCase()}
                </span>
              </td>
              <td>
                {record.action === 'create' ? (
                  <span className="text-muted">Asset created</span>
                ) : (
                  <span>
                    <strong>{record.field_name}</strong> changed from{' '}
                    <span className="text-danger text-decoration-line-through">{record.old_value || '(empty)'}</span> to{' '}
                    <span className="text-success">{record.new_value}</span>
                  </span>
                )}
              </td>
              <td>{record.changed_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AddEquipment;
